import React from 'react';
import { Typography, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';

const healthLevels = [
  { key: 'STRONG', color: '#059669' },
  { key: 'HEALTHY', color: '#16a34a' },
  { key: 'WEAK', color: '#d97706' },
  { key: 'UNHEALTHY', color: '#dc2626' },
];

/* Pulse icon SVG */
const PulseIcon = ({ color }) => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 12h4l3-9 4 18 3-9h4" />
  </svg>
);

/* ── Health Status Card ─── */
function HealthCard({ marketHealth, healthNote, t }) {
  const activeIdx = healthLevels.findIndex(l => l.key === marketHealth);
  const active = healthLevels[activeIdx >= 0 ? activeIdx : 2];

  return (
    <Box sx={{
      p: 2, borderRadius: 2.5, border: '1px solid', borderColor: 'divider',
      bgcolor: 'background.paper', display: 'flex', flexDirection: 'column', gap: 1.5,
    }}>
      {/* Top row: icon + status text */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Box sx={{
          width: 44, height: 44, borderRadius: '50%',
          bgcolor: `${active.color}12`, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <PulseIcon color={active.color} />
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{
            textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem',
          }}>
            {t('marketBreadth.cards.marketHealth')}
          </Typography>
          <Typography sx={{ fontSize: '1.15rem', fontWeight: 800, color: active.color, lineHeight: 1.2 }}>
            {t(`marketBreadth.healthLevel.${active.key?.toLowerCase()}`)}
          </Typography>
        </Box>
      </Box>

      {/* Health note (short-term overlay) */}
      {healthNote && (
        <Typography variant="caption" sx={{
          color: active.color, fontSize: '0.68rem', fontWeight: 500,
          lineHeight: 1.3, opacity: 0.85,
          bgcolor: `${active.color}08`, borderRadius: 1.5, px: 1, py: 0.5,
        }}>
          ⚠ {healthNote}
        </Typography>
      )}

      {/* 4-step indicator bar */}
      <Box sx={{ display: 'flex', gap: '3px', height: 6, borderRadius: 3, overflow: 'hidden' }}>
        {healthLevels.map((lvl, i) => (
          <Box key={lvl.key} sx={{
            flex: 1, borderRadius: 3,
            bgcolor: i <= activeIdx ? active.color : `${active.color}15`,
            opacity: i <= activeIdx ? (i === activeIdx ? 1 : 0.45) : 1,
            transition: 'all .3s',
          }} />
        ))}
      </Box>
    </Box>
  );
}

/* ── Sector Dots Card ─── */
function BreadthCard({ label, pct, count, total, color }) {
  const filled = Math.max(0, Math.min(count, total));
  const dots = Array.from({ length: total }, (_, i) => i < filled);

  return (
    <Box sx={{
      p: 2, borderRadius: 2.5, border: '1px solid', borderColor: 'divider',
      bgcolor: 'background.paper', display: 'flex', flexDirection: 'column', gap: 1,
    }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{
        textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem',
      }}>
        {label}
      </Typography>

      {/* Big % + fraction */}
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
        <Typography sx={{
          fontSize: '1.6rem', fontWeight: 800, color, lineHeight: 1,
          fontFeatureSettings: '"tnum"',
        }}>
          {pct?.toFixed(0) || 0}
          <Typography component="span" sx={{ fontSize: '0.85rem', fontWeight: 600, color: 'text.disabled', ml: 0.25 }}>
            %
          </Typography>
        </Typography>
        <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.72rem', fontWeight: 500 }}>
          ({filled}/{total})
        </Typography>
      </Box>

      {/* Sector dots */}
      <Box sx={{ display: 'flex', gap: '5px', flexWrap: 'wrap', mt: 0.25 }}>
        {dots.map((active, i) => (
          <Box key={i} sx={{
            width: 18, height: 18, borderRadius: '50%',
            bgcolor: active ? color : `${color}12`,
            border: active ? 'none' : `1.5px dashed ${color}35`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'all .3s',
          }}>
            {active && (
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#fff' }} />
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/* ── Stock Breadth Bar Card ─── */
function StockBreadthCard({ label, pct, total, color, t }) {
  const barPct = Math.max(0, Math.min(100, pct || 0));
  const isGood = barPct >= 50;

  return (
    <Box sx={{
      p: 2, borderRadius: 2.5, border: '1px solid', borderColor: 'divider',
      bgcolor: 'background.paper', display: 'flex', flexDirection: 'column', gap: 1,
    }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{
        textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem',
      }}>
        {label}
      </Typography>

      {/* Big % */}
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
        <Typography sx={{
          fontSize: '1.6rem', fontWeight: 800, color: isGood ? color : '#ef4444', lineHeight: 1,
          fontFeatureSettings: '"tnum"',
        }}>
          {pct?.toFixed(1) || 0}
          <Typography component="span" sx={{ fontSize: '0.85rem', fontWeight: 600, color: 'text.disabled', ml: 0.25 }}>
            %
          </Typography>
        </Typography>
        {total > 0 && (
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.72rem', fontWeight: 500 }}>
            ({total} {t('marketBreadth.cards.stocksUnit')})
          </Typography>
        )}
      </Box>

      {/* Progress bar */}
      <Box sx={{ width: '100%', mt: 0.5 }}>
        <Box sx={{
          height: 8, borderRadius: 4, bgcolor: `${color}15`, overflow: 'hidden', position: 'relative',
        }}>
          <Box sx={{
            height: '100%', borderRadius: 4,
            width: `${barPct}%`,
            bgcolor: isGood ? color : '#ef4444',
            transition: 'width 0.5s ease',
          }} />
          {/* 50% marker */}
          <Box sx={{
            position: 'absolute', top: 0, left: '50%', width: 2, height: '100%',
            bgcolor: 'text.disabled', opacity: 0.3,
          }} />
        </Box>
      </Box>
    </Box>
  );
}

/* ── Main Component ─── */
function MarketHealthCards({ marketHealth, latestBreadth }) {
  const { t } = useTranslation();

  const hasStockData = latestBreadth.stock_pct_above_ma20 != null && latestBreadth.total_stocks_ma20 > 0;

  return (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
      gap: { xs: 1.5, md: 2 },
      mb: { xs: 2, md: 2.5 },
    }}>
      <HealthCard marketHealth={marketHealth} healthNote={latestBreadth.health_note} t={t} />

      {hasStockData ? (
        <>
          <StockBreadthCard
            label={t('marketBreadth.cards.stocksAboveMA20')}
            pct={latestBreadth.stock_pct_above_ma20}
            total={latestBreadth.total_stocks_ma20}
            color="#6366f1"
            t={t}
          />
          <StockBreadthCard
            label={t('marketBreadth.cards.stocksAboveMA50')}
            pct={latestBreadth.stock_pct_above_ma50}
            total={latestBreadth.total_stocks_ma50}
            color="#10b981"
            t={t}
          />
        </>
      ) : (
        <>
          <BreadthCard
            label={t('marketBreadth.cards.sectorsAboveMA20')}
            pct={latestBreadth.pct_above_ma20}
            count={latestBreadth.sectors_above_ma20 || 0}
            total={latestBreadth.total_sectors || 11}
            color="#6366f1"
          />
          <BreadthCard
            label={t('marketBreadth.cards.sectorsAboveMA50')}
            pct={latestBreadth.pct_above_ma50}
            count={latestBreadth.sectors_above_ma50 || 0}
            total={latestBreadth.total_sectors || 11}
            color="#10b981"
          />
        </>
      )}
    </Box>
  );
}

export default MarketHealthCards;
